import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { selectSelectedTask } from "../../../slice/taskSlice";
import { getTaskById, updateTask } from "../../../Api/taskApi";
import { fetchAllOrganizations } from "../../../slice/organizationsSlice";
import { useToast } from "../../../components/Toast";
import TaskTitles from "./atomic/TaskTitles";
import TaskInput from "./atomic/TaskInput";
import TN from "../assets/tn.svg";
import Describe from "../assets/describe.svg";
import Solution from "../assets/solution.svg";
import Goal from "../assets/goal.svg";
import KPI from "../assets/kpi.svg";
import Process from "../assets/process.svg";
import Toggle from "./atomic/Toggle";
import SaveButton from "./atomic/SaveButton";

const Overview = () => {
  const dispatch = useDispatch();
  const { addToast, ToastContainer } = useToast();

  // Get selected task from Redux store
  const selectedTask = useSelector(selectSelectedTask);
  const { data, loading, error } = useSelector((state) => state.admin);
  const { organizations } = useSelector((state) => state.organizations);

  // State for form inputs and loading
  const [taskName, setTaskName] = useState("");
  const [problem, setProblem] = useState("");
  const [solution, setSolution] = useState("");
  const [goal, setGoal] = useState("");
  const [kpi, setKpi] = useState("");
  const [manager, setManager] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [fileUploadRequired, setFileUploadRequired] = useState(false);
  const [selectedOrganization, setSelectedOrganization] = useState("");

  // Fetch task details whenever selectedTask changes


  const isAdmin = data?.user_role === "SUPER_ADMIN" || data?.user_role === "ADMIN";
  const isSuperAdmin = data?.user_role === "SUPER_ADMIN";
  const userOrganizationId = data?.organization_id;
  
  const canAdminEdit = isAdmin && !isSuperAdmin && selectedTask?.organization_id === userOrganizationId;
  const canAdminCreate = isSuperAdmin;

  // Fetch organizations when component mounts
  useEffect(() => {
    dispatch(fetchAllOrganizations());
  }, [dispatch]);

  useEffect(() => {
    const fetchTaskDetails = async () => {
      if (selectedTask) {
        try {
          setIsLoading(true);
          const fetchedTask = await getTaskById(selectedTask.id);

          // Update form with the latest data from API
          setTaskName(fetchedTask.name || "");
          setProblem(fetchedTask.problem || "");
          setSolution(fetchedTask.solution || "");
          setGoal(fetchedTask.goal || "");
          setKpi(fetchedTask.kpi || "");
          setManager(fetchedTask.manager || "");
          setFileUploadRequired(fetchedTask.file_upload_required || false);
          setSelectedOrganization(fetchedTask.organization_id || "");
        } catch (error) {
          addToast("Failed to fetch task details", "error");
          console.error("Fetch task error:", error);
        } finally {
          setIsLoading(false);
        }
      }
    };

    fetchTaskDetails();
  }, [selectedTask]);

  // Handle task update
  const handleSaveTask = async () => {
    if (!selectedTask) {
      addToast("No task selected", "error");
      return;
    }

    // Check permissions for ADMIN users
    if (isAdmin && !isSuperAdmin) {
      // ADMIN users can only edit tasks assigned to their organization
      if (selectedTask.organization_id && selectedTask.organization_id !== userOrganizationId) {
        addToast("Sie können nur Aufgaben Ihrer Organisation bearbeiten", "error");
        return;
      }
      
      // ADMIN users cannot assign tasks to other organizations
      if (selectedOrganization && selectedOrganization !== userOrganizationId) {
        addToast("Sie können Aufgaben nur Ihrer Organisation zuweisen", "error");
        return;
      }
    }

    setIsLoading(true);

    try {
      const taskData = {
        id: String(selectedTask.id),
        name: taskName,
        problem,
        solution,
        goal,
        kpi,
        manager,
        file_upload_required: fileUploadRequired,
        organization_id: selectedOrganization || null,
      };

      // Update task
      await updateTask(taskData);

      // Fetch the updated task details
      const updatedTask = await getTaskById(selectedTask.id);

      // Update form with the latest data from API
      setTaskName(updatedTask.name || "");
      setProblem(updatedTask.problem || "");
      setSolution(updatedTask.solution || "");
      setGoal(updatedTask.goal || "");
      setKpi(updatedTask.kpi || "");
      setManager(updatedTask.manager || "");

      addToast("Aufgabe erfolgreich aktualisiert", "success");
    } catch (error) {
      addToast(error.message || "Failed to update task", "error");
      console.error("Update task error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div>
      <ToastContainer />
      <div className="flex justify-between border-b border-gray-200 pb-10">
        <TaskTitles
          title="Aufgabenname"
          description="Geben Sie Ihrer Aufgabe einen 
Namen."
        />
        <div className="w-[60%]">
          <TaskInput
            placeholder="Geben Sie den Aufgabentitel 
ein."
            title="Namen hinzufügen"
            icon={TN}
            value={taskName}
            onChange={(e) => setTaskName(e.target.value)}
            disabled={!isAdmin || (!canAdminCreate && !canAdminEdit)}
          />
        </div>
      </div>
      <div className="flex justify-between border-b border-gray-200 pb-10 pt-10">
        <TaskTitles
          title="Organisation"
          description="Wählen Sie eine Organisation für diese Aufgabe (optional)."
        />
        <div className="w-[60%]">
          <div className="flex flex-col gap-2">
            <label className="text-sm font-medium text-gray-700">
              Organisation zuweisen
            </label>
            <select
              value={selectedOrganization}
              onChange={(e) => setSelectedOrganization(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-custom-yellow focus:border-transparent transition-colors duration-200"
              disabled={!isSuperAdmin}
            >
              <option value="">Global (für alle Benutzer)</option>
              {organizations.map((org) => {
                // For ADMIN users, only show their organization
                if (isAdmin && !isSuperAdmin && org.organization_id !== userOrganizationId) {
                  return null;
                }
                return (
                  <option key={org.organization_id} value={org.organization_id}>
                    {org.organization_name}
                  </option>
                );
              })}
            </select>
          </div>
        </div>
      </div>
      <div className="flex justify-between border-b border-gray-200 pb-10 pt-10">
        <TaskTitles
          title="Problem und Lösung beschreiben"
          description="Beschreiben Sie das Problem und die 
Lösung des Anwendungsfalls."
        />
        <div className="w-[60%] flex flex-col gap-4">
          <TaskInput
            placeholder="Beschreiben Sie das 
zugrundeliegende Problem des 
Anwendungsfalls."
            title="Problem beschreiben"
            icon={Describe}
            value={problem}
            onChange={(e) => setProblem(e.target.value)}
            disabled={!isAdmin || (!canAdminCreate && !canAdminEdit)}
          />
          <TaskInput
            placeholder="Beschreiben Sie die avisierte 
Lösung des Anwendungsfalls."
            title="Lösung beschreiben"
            icon={Solution}
            value={solution}
            onChange={(e) => setSolution(e.target.value)}
            disabled={!isAdmin || (!canAdminCreate && !canAdminEdit)}
          />
        </div>
      </div>
      <div className="flex justify-between border-b border-gray-200 pb-10 pt-10">
        <TaskTitles
          title="Ziele und KPIs beschreiben"
          description="Beschreiben Sie das Ziel und die KPIs 
des Anwendungsfalls."
        />
        <div className="w-[60%] flex flex-col gap-4">
          <TaskInput
            placeholder="Beschreiben Sie das Ziel des 
Anwendungsfalls."
            title="Ziel des Anwendungsfalles 
beschreiben"
            icon={Goal}
            value={goal}
            onChange={(e) => setGoal(e.target.value)}
            disabled={!isAdmin || (!canAdminCreate && !canAdminEdit)}
          />
          <TaskInput
            placeholder="Beschreiben Sie die KPIs hinter 
dem Anwendungsfall."
            title="KPIs beschreiben"
            icon={KPI}
            value={kpi}
            onChange={(e) => setKpi(e.target.value)}
            disabled={!isAdmin || (!canAdminCreate && !canAdminEdit)}
          />
          <TaskInput
            placeholder="Geben Sie den Namen des 
Prozessmanagers ein."
            title="Prozessmanager"
            icon={Process}
            value={manager}
            onChange={(e) => setManager(e.target.value)}
            disabled={!isAdmin || (!canAdminCreate && !canAdminEdit)}
          />
        </div>
      </div>
      <div className="flex justify-between border-b border-gray-200 pb-10 pt-10">
        <TaskTitles
          title="Datei-Upload Konfiguration"
          description="Konfigurieren Sie, ob diese Aufgabe Datei-Uploads von Benutzern erfordert."
        />
        <div className="w-[60%] flex items-center">
          <Toggle
            isOn={fileUploadRequired}
            onToggle={() => setFileUploadRequired(!fileUploadRequired)}
            label="Datei-Upload erforderlich"
            disabled={!isAdmin || (!canAdminCreate && !canAdminEdit)}
          />
        </div>
      </div>
      <div className="flex justify-end pt-5 pb-10">
        <SaveButton
          onClick={handleSaveTask}
          isLoading={isLoading}
          disabled={isLoading}
        />
      </div>
    </div>
  );
};

export default Overview;